import React, { useState, useCallback, useMemo, KeyboardEvent, useRef, useEffect } from 'react'
import { cm } from '~/utils/utils'
import { SearchInputIcon, CloseCircleFilledIcon } from '../icons'

export interface SearchInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onClear'> {
  /** Current search value */
  value?: string
  /** Placeholder text for the input */
  placeholder?: string
  /** Called when the input value changes with trimmed value */
  onChange?: (value: string) => void
  /** Called when the clear button is clicked or Esc is pressed */
  onClear?: () => void
  /** Called when Enter is pressed (optional jump to top result) */
  onEnter?: () => void
  /** Additional CSS classes */
  className?: string
  /** Whether the search field should fill its parent instead of using the Figma default width. */
  fullWidth?: boolean
}

export interface SearchResult {
  id: string
  label: string
  [key: string]: any
}

export interface SearchWithResultsProps extends SearchInputProps {
  /** Data source for search results */
  data?: SearchResult[]
  /** Called when a result is selected */
  onResultSelect?: (result: SearchResult) => void
  /** Maximum number of results to show */
  maxResults?: number
  /** Custom render function for highlighting matches */
  renderResult?: (result: SearchResult, query: string, highlightedLabel: string) => React.ReactNode
}

/**
 * SearchInput Component
 *
 * A search input component with real-time filtering capabilities, keyboard support,
 * and visual feedback states.
 */
export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value = '',
      placeholder = 'Search...',
      onChange,
      onClear,
      onEnter,
      className,
      fullWidth = false,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false)
    const [isTextOverflowing, setIsTextOverflowing] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const canvasRef = useRef<HTMLCanvasElement | null>(null)

    // Determine if input has content (trimmed)
    const hasContent = useMemo(() => value.trim().length > 0, [value])

    // Function to measure text width
    const measureTextWidth = useCallback((text: string, font: string): number => {
      if (!canvasRef.current) {
        canvasRef.current = document.createElement('canvas')
      }
      const context = canvasRef.current.getContext('2d')
      if (!context) return 0

      context.font = font
      return context.measureText(text).width
    }, [])

    // Function to get computed font style from input element
    const getInputFont = useCallback((): string => {
      if (!inputRef.current) return '16px Arial'

      const computedStyle = window.getComputedStyle(inputRef.current)
      return `${computedStyle.fontSize} ${computedStyle.fontFamily}`
    }, [])

    // Check if text is overflowing (filling up the input)
    const checkTextOverflow = useCallback(() => {
      if (!inputRef.current || !value.trim()) {
        setIsTextOverflowing(false)
        return
      }

      const input = inputRef.current
      const inputRect = input.getBoundingClientRect()

      // Calculate available width with normal padding first (8px left, 32px right for icon)
      // const normalAvailableWidth = inputRect.width - 8 - 32
      const normalAvailableWidth = inputRect.width

      // Measure text width
      const font = getInputFont()
      const textWidth = measureTextWidth(value, font)

      // Consider text as "full" when it takes up more than 99% of normal available space
      // This means the text is almost completely filling the input with normal padding
      const threshold = normalAvailableWidth * 0.99
      setIsTextOverflowing(textWidth >= threshold)
    }, [value, measureTextWidth, getInputFont])

    // Handle external ref forwarding
    React.useImperativeHandle(ref, () => inputRef.current!)

    // Check overflow when value changes or component mounts
    useEffect(() => {
      checkTextOverflow()
    }, [checkTextOverflow])

    // Also check on resize
    useEffect(() => {
      const handleResize = () => {
        checkTextOverflow()
      }

      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }, [checkTextOverflow])

    // Handle input changes with trimming for matching logic
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value
        onChange?.(newValue)
      },
      [onChange]
    )

    // Handle clear action
    const handleClear = useCallback(() => {
      onChange?.('')
      onClear?.()
    }, [onChange, onClear])

    // Handle keyboard events
    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
          e.preventDefault()
          handleClear()
        } else if (e.key === 'Enter') {
          e.preventDefault()
          onEnter?.()
        }
      },
      [handleClear, onEnter]
    )

    // Update border color logic based on focus state
    const borderColor = useMemo(() => {
      // Border is highlighted only when input is focused
      if (isFocused) {
        return 'border-tokens-status-link-1'
      }
      return 'border-tokens-neutral-border-1'
    }, [isFocused])

    // Dynamic padding based on text overflow state
    const paddingStyle = useMemo(() => {
      const leftPadding = isTextOverflowing ? '0px' : '8px'
      return {
        padding: `8px 32px 8px ${leftPadding}` // Dynamic left padding, fixed right padding for icon
      }
    }, [isTextOverflowing])

    return (
      <div
        className={cm(
          'relative h-9 bg-tokens-neutral-bg-white rounded-[12px] border border-solid flex items-center',
          fullWidth ? 'w-full' : 'w-[280px]',
          borderColor,
          className
        )}
        style={paddingStyle}
      >
        <input
          ref={inputRef}
          type='text'
          value={value}
          placeholder={placeholder}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className={cm(
            'w-full h-full bg-transparent border-none outline-none',
            'text-base leading-4 text-tokens-neutral-text-primary',
            'placeholder:text-base placeholder:leading-[22px] placeholder:text-tokens-neutral-text-placeholder placeholder:font-medium',
            'whitespace-nowrap'
          )}
          {...props}
        />

        {/* Right icon - Search or Clear */}
        <div className='absolute right-[10px] top-1/2 transform -translate-y-1/2 flex items-center justify-center w-4 h-4'>
          {hasContent && isFocused ? (
            <button
              type='button'
              onClick={handleClear}
              onMouseDown={e => {
                // Prevent the input from losing focus when clicking the clear button
                e.preventDefault()
              }}
              className='w-4 h-4 flex items-center justify-center cursor-pointer'
              aria-label='Clear search'
            >
              <CloseCircleFilledIcon className='w-4 h-4' />
            </button>
          ) : (
            <SearchInputIcon className='w-4 h-4' />
          )}
        </div>
      </div>
    )
  }
)

SearchInput.displayName = 'SearchInput'

/**
 * Highlights the first matching substring in a label
 * @param label The text to highlight
 * @param query The search query
 * @returns React element with highlighted match
 */
export const highlightMatch = (label: string, query: string): React.ReactNode => {
  if (!query.trim()) {
    return label
  }

  const normalizedQuery = query.toLowerCase().trim()
  const normalizedLabel = label.toLowerCase()
  const matchIndex = normalizedLabel.indexOf(normalizedQuery)

  if (matchIndex === -1) {
    return label
  }

  const beforeMatch = label.slice(0, matchIndex)
  const match = label.slice(matchIndex, matchIndex + query.length)
  const afterMatch = label.slice(matchIndex + query.length)

  return (
    <>
      {beforeMatch}
      <mark className='bg-yellow-200 px-0'>{match}</mark>
      {afterMatch}
    </>
  )
}

/**
 * SearchInput with Results Component
 *
 * Extends SearchInput with real-time filtering and result display
 */
export const SearchInputWithResults: React.FC<SearchWithResultsProps> = ({
  data = [],
  onResultSelect,
  maxResults = 50,
  renderResult,
  value = '',
  onChange,
  ...searchProps
}) => {
  // Filter results based on search query
  const filteredResults = useMemo(() => {
    const trimmedQuery = value.trim()

    // Edge case: treat input with only spaces as empty
    if (!trimmedQuery) {
      return []
    }

    const normalizedQuery = trimmedQuery.toLowerCase()

    return data
      .filter(item => item.label.toLowerCase().includes(normalizedQuery))
      .slice(0, maxResults)
  }, [data, value, maxResults])

  // Handle result selection
  const handleResultSelect = useCallback(
    (result: SearchResult) => {
      onResultSelect?.(result)
      onChange?.(result.label) // Optionally set the input value to the selected result
    },
    [onResultSelect, onChange]
  )

  return (
    <div className='relative'>
      <SearchInput value={value} onChange={onChange} {...searchProps} />

      {/* Results dropdown */}
      {filteredResults.length > 0 && (
        <div className='absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50'>
          {filteredResults.map(result => (
            <div
              key={result.id}
              onClick={() => handleResultSelect(result)}
              className='px-3 py-2 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0'
            >
              {renderResult
                ? renderResult(result, value, highlightMatch(result.label, value) as string)
                : highlightMatch(result.label, value)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
